import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Car } from '@phosphor-icons/react';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import SearchBar from '../../components/SearchBar';
import { matchCarsByQuery } from '../../utils/localCarSearch';
import './HeroSearch.css';
import { useTranslation } from '../../i18n/I18nContext';
import { numberLocale } from '../../utils/formatMoney';
import { config } from '../../config/runtimeConfig';

const ANY = '';

/** Шаги потолка цены. Отдаём круглые числа, а не сырые цены из стока. */
const PRICE_STEPS = [15000, 20000, 25000, 30000, 40000, 50000, 75000, 100000];

/**
 * Поиск по стоку в герое — форма из полей, как у европейских дилеров.
 *
 * ## Почему форма, а не одна строка
 *
 * Разбор десяти дилерских сайтов (#1330): у всех первый экран — это **поля**
 * (марка, модель, потолок цены, кузов) плюс счётчик стока рядом с кнопкой.
 * Van Mossel пишет «12 419 авто в наличии» прямо на кнопке поиска. Одна
 * текстовая строка — паттерн маркетплейса, а не дилера: дилер не узнаёт в ней
 * свой сайт.
 *
 * ## Два входа на одной плашке
 *
 * 1. **Поля + «Show N cars»** — знакомый дилерский путь, ведёт в каталог с
 *    проставленными фильтрами.
 * 2. **«Ask the assistant»** — то же намерение, другим способом.
 *
 * Оба стоят рядом: это два способа сделать одно и то же, и разносить их по
 * разным рядам значило показывать ассистента второстепенным.
 *
 * ## Строка поиска
 *
 * Текстовый поиск витрины живёт здесь, а не в шапке и не над фильтрами
 * каталога: это первый вход в сток, и он стоит там, где покупатель начинает.
 * Строка — часть той же формы: её текст уходит в каталог (`?search=`) вместе
 * с выбранными полями, и Enter в ней, и кнопка дают одну и ту же выдачу.
 * Подсказки по мере ввода ведут сразу в карточку машины.
 *
 * На широкой плашке строка — первая ячейка ОДНОГО ряда с полями и кнопкой.
 * Пока в ней фокус и текст, она раскрывается до кнопки (`--query-open`):
 * поля уходят, подсказки открываются вниз. Enter отправляет запрос вместе с
 * выбранными полями — они не сбрасываются, а только скрыты. Escape и Tab
 * вперёд сворачивают строку, фокус остаётся в поле.
 *
 * ## Счётчик
 *
 * Считается по загруженному стоку теми же правилами, что фильтрует каталог, —
 * иначе кнопка обещала бы одно, а страница показывала другое.
 *
 * @param {object} props
 * @param {Array<object>} props.cars — загруженный инвентарь.
 */

export const HeroSearch = ({ cars = [], panel = true }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [make, setMake] = useState(ANY);
  const [model, setModel] = useState(ANY);
  const [maxPrice, setMaxPrice] = useState(ANY);
  const [query, setQuery] = useState('');
  const formatPrice = (value) =>
    new Intl.NumberFormat(numberLocale(language)).format(value);
  // Строка раскрывается на всю форму по фокусу — сразу по клику, а не после
  // первой буквы (#1335). Tab вперёд сворачивает её ДО того, как фокус уйдёт
  // дальше (`collapsed`), поэтому поля снова в порядке обхода и Tab их не
  // перепрыгивает. Escape тоже сворачивает, следующий ввод раскрывает снова;
  // фокус при этом остаётся в поле.
  const [queryFocused, setQueryFocused] = useState(false);
  const [queryCollapsed, setQueryCollapsed] = useState(false);
  // У дилера по токену (`DealerHomePage`) — как на main: строка раскрывается,
  // только когда в ней уже есть текст.
  const queryOpen =
    queryFocused &&
    !queryCollapsed &&
    (config.IS_OWN_STOCK || query.trim().length > 0);
  // Строка поиска шлёт запрос от двух символов — с той же границы его
  // учитывают счётчик и отправка, иначе кнопка считала бы по «k».
  const term = query.trim().length >= 2 ? query.trim() : '';

  const makes = useMemo(
    () => [...new Set(cars.map((car) => car.make).filter(Boolean))].sort(),
    [cars]
  );

  // Модели зависят от выбранной марки: показывать чужие модели значит
  // предлагать заведомо пустую выдачу.
  const models = useMemo(() => {
    const pool = make ? cars.filter((car) => car.make === make) : cars;
    return [...new Set(pool.map((car) => car.model).filter(Boolean))].sort();
  }, [cars, make]);

  // Потолок цены показываем только до максимума стока: пункт «до €100 000»
  // при самой дорогой машине за €52 900 — это шум.
  const priceOptions = useMemo(() => {
    const top = Math.max(0, ...cars.map((car) => Number(car.price) || 0));
    return PRICE_STEPS.filter(
      (step, index) => step <= top || PRICE_STEPS[index - 1] < top
    );
  }, [cars]);

  // Текст запроса считаем тем же правилом, что каталог, когда Meilisearch
  // выключен (`localCarSearch`). С включённым Meilisearch каталог может найти
  // больше (опечатки, морфология) — кнопка тогда обещает нижнюю границу.
  const matches = useMemo(
    () =>
      (term ? matchCarsByQuery(cars, term) : cars).filter((car) => {
        if (make && car.make !== make) return false;
        if (model && car.model !== model) return false;
        if (maxPrice && Number(car.price) > Number(maxPrice)) return false;
        return true;
      }).length,
    [cars, term, make, model, maxPrice]
  );

  const handleMakeChange = (value) => {
    setMake(value);
    setModel(ANY); // модель прежней марки к новой не относится
  };

  const goToCatalog = (search) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (make) params.set('make', make);
    if (model) params.set('model', model);
    if (maxPrice) params.set('maxPrice', maxPrice);
    const qs = params.toString();
    navigate(qs ? `/cars?${qs}` : '/cars');
  };

  const submit = (event) => {
    event.preventDefault();
    goToCatalog(term);
  };

  return (
    <div className="hero-search">
      <form
        className={`hero-search__form${
          panel ? ' hero-search__form--panel' : ''
        }${queryOpen ? ' hero-search__form--query-open' : ''}`}
        onSubmit={submit}
      >
        {/* Фокус считаем по всему блоку, а не по полю: клик по подсказке
            переводит фокус на её кнопку, и строка не должна схлопнуться
            раньше, чем сработает переход. */}
        <div
          className="hero-search__query"
          onFocus={() => setQueryFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setQueryFocused(false);
              setQueryCollapsed(false);
            }
          }}
          onKeyDown={(e) => {
            // Tab вперёд: поля должны стать видимыми ДО того, как фокус
            // уйдёт дальше, — иначе он перепрыгнет их на кнопку.
            if (e.key === 'Escape' || (e.key === 'Tab' && !e.shiftKey)) {
              setQueryCollapsed(true);
            }
          }}
        >
          <SearchBar
            variant="standalone"
            placeholder={t(
              'showcase.catalog.searchPlaceholder',
              'Search by make or model'
            )}
            onQueryChange={(value) => {
              setQuery(value);
              setQueryCollapsed(false);
            }}
            // Крестик зовёт колбэк с пустой строкой — в герое это просто
            // очистка поля, а не переход в каталог.
            onSubmitQuery={(value) => value && goToCatalog(value)}
            force
            fallbackCars={cars}
          />
        </div>

        <label className="hero-search__field">
          <span className="hero-search__label">
            {t('showcase.search.make', 'Make')}
          </span>
          <Select
            value={make}
            onChange={(e) => handleMakeChange(e.target.value)}
          >
            <option value={ANY}>
              {t('showcase.search.anyMake', 'Any make')}
            </option>
            {makes.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </label>

        <label className="hero-search__field">
          <span className="hero-search__label">
            {t('showcase.search.model', 'Model')}
          </span>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={models.length === 0}
          >
            <option value={ANY}>
              {t('showcase.search.anyModel', 'Any model')}
            </option>
            {models.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </Select>
        </label>

        <label className="hero-search__field">
          <span className="hero-search__label">
            {t('showcase.search.maxPrice', 'Max price')}
          </span>
          <Select
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          >
            <option value={ANY}>
              {t('showcase.search.anyPrice', 'Any price')}
            </option>
            {priceOptions.map((step) => (
              <option key={step} value={step}>
                {t('showcase.search.priceUpTo', 'up to €{price}').replace(
                  '{price}',
                  formatPrice(step)
                )}
              </option>
            ))}
          </Select>
        </label>

        <div className="hero-search__actions">
          {/* Таблетка со стрелкой — язык героя S2 (#1335). Иконка машины
              снята: стрелка в круге сама говорит «перейти к машинам». */}
          <Button
            type="submit"
            size="lg"
            arrow={config.IS_OWN_STOCK}
            className="hero-search__submit"
          >
            {/* У дилера — иконка машины слева, как на main. */}
            {!config.IS_OWN_STOCK && (
              <Car size={18} weight="bold" aria-hidden="true" />
            )}
            {matches > 0
              ? t(
                  matches === 1
                    ? 'showcase.search.showOne'
                    : 'showcase.search.showMany',
                  matches === 1 ? 'Show {count} car' : 'Show {count} cars'
                ).replace('{count}', matches)
              : t('showcase.search.cta', 'Search')}
          </Button>
        </div>
      </form>
    </div>
  );
};

HeroSearch.propTypes = {
  cars: PropTypes.arrayOf(PropTypes.object),
  /**
   * Рисовать ли под полями подложку с полем и скруглением. `false` — поля
   * лежат прямо на фоне страницы: так форма стоит в герое, где фон полосы
   * сам по себе серый и вторая подложка на нём читается лишней коробкой.
   */
  panel: PropTypes.bool,
};

export default HeroSearch;
